import React, { useState } from "react";
import {
  MessageBar,
  MessageBarType,
  Toggle,
  Dropdown,
  IDropdownOption,
} from "@fluentui/react";
import SaveButton from "@/ui-controls/SaveButton";
import CancelButton from "@/ui-controls/CancelButton";
import TextInput from "@/ui-controls/TextInput";
import { Grid } from "fluentui-react-grid";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  IntegrationDto,
  IntegrationTagDto,
  IntegrationTagMappingDto,
  updateIntegration,
  createIntegrationTagMapping,
  deleteIntegrationTagMapping,
} from "@/api/integrations";

interface Props {
  integration: IntegrationDto;
  tagMappings: IntegrationTagMappingDto[];
  tags: IntegrationTagDto[];
  onSave?: () => void;
  onClose?: () => void;
}

const EditIntegrationForm = (props: Props) => {
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const currentTagIds = props.tagMappings.map((m) => m.integrationTagId);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(currentTagIds);

  const tagOptions: IDropdownOption[] = props.tags
    .filter((t) => t.isActive)
    .map((t) => ({ key: t.id, text: t.name }));

  const formik = useFormik({
    initialValues: {
      name: props.integration.name,
      isActive: props.integration.isActive,
    },
    enableReinitialize: false,
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required").max(250),
    }),
    onSubmit: async (values) => {
      setIsSaving(true);
      setErrorMessage("");
      try {
        await updateIntegration({
          id: props.integration.id,
          code: props.integration.code,
          name: values.name,
          isActive: values.isActive,
        });

        // Sync tag mappings: add new, remove old
        const toAdd = selectedTagIds.filter(
          (id) => !currentTagIds.includes(id)
        );
        const toRemove = props.tagMappings.filter(
          (m) => !selectedTagIds.includes(m.integrationTagId)
        );

        await Promise.all([
          ...toAdd.map((tagId) =>
            createIntegrationTagMapping({
              integrationId: props.integration.id,
              integrationTagId: tagId,
            })
          ),
          ...toRemove.map((m) => deleteIntegrationTagMapping(m.id)),
        ]);

        props.onSave?.();
      } catch (error: any) {
        const data = error?.response?.data;
        if (data?.detail) {
          setErrorMessage(data.detail);
        } else {
          setErrorMessage("Failed to update integration.");
        }
      } finally {
        setIsSaving(false);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid dir="ltr">
        {errorMessage && (
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <MessageBar
                messageBarType={MessageBarType.error}
                onDismiss={() => setErrorMessage("")}
              >
                {errorMessage}
              </MessageBar>
            </Grid.Col>
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <TextInput
              label="Code"
              baseTypeProps={{
                disabled: true,
                value: props.integration.code,
              }}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <TextInput
              label="Name"
              baseTypeProps={{
                disabled: isSaving,
                ...formik.getFieldProps("name"),
              }}
              hasError={formik.errors.name && formik.touched.name}
              errorMessage={formik.errors.name}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <Dropdown
              label="Tags"
              placeholder="Select tags"
              options={tagOptions}
              multiSelect
              disabled={isSaving}
              selectedKeys={selectedTagIds}
              onChange={(_, option) => {
                if (option) {
                  setSelectedTagIds((prev) =>
                    option.selected
                      ? [...prev, option.key as number]
                      : prev.filter((k) => k !== option.key)
                  );
                }
              }}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <Toggle
              label="Active"
              checked={formik.values.isActive}
              onChange={(_, checked) =>
                formik.setFieldValue("isActive", checked ?? false)
              }
              disabled={isSaving}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row className="panel-buttons">
          <Grid.Col>
            <SaveButton
              text="Save"
              baseTypeProps={{ type: "submit", disabled: isSaving }}
            />
          </Grid.Col>
          <Grid.Col>
            <CancelButton
              text="Cancel"
              onClick={() => props.onClose?.()}
              baseTypeProps={{ disabled: isSaving }}
            />
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </form>
  );
};

export default EditIntegrationForm;
